import React, { useEffect, useState } from "react";
import { assets, JobCategories, JobLocations } from "../assets/assets";
import JobCard from "./JobCard";
import Loading from "./Loading"
import AxiosInstance from "./AxiosInstance";

const JobListing = ({ searchFilter, isSearched, onClearFilter }) => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // Fetch jobs on mount
  useEffect(() => {
     const fetchJobs = async () => {
      setLoading(true); // ✅ Start loading
      try {
        const res = await AxiosInstance.get("/jobs/");
        const data = res.data;
        const visibleJobs = data.filter((job) => job.visible);
        setJobs(visibleJobs);
        setFilteredJobs(visibleJobs.slice().reverse());
      } catch (err) {
        console.error("Failed to fetch jobs:", err);
      }
      setLoading(false); // ✅ End loading
    };

    fetchJobs();
  }, []);

  // Apply filters
  useEffect(() => {
    const matchesCategory = (job) =>
      selectedCategories.length === 0 ||
      selectedCategories.includes(job.category);

    const matchesLocation = (job) =>
      selectedLocations.length === 0 ||
      selectedLocations.includes(job.location);

    const matchesTitle = (job) =>
      searchFilter.title === "" ||
      job.title.toLowerCase().includes(searchFilter.title.toLowerCase());

    const matchesSearchLocation = (job) =>
      searchFilter.location === "" ||
      job.location.toLowerCase().includes(searchFilter.location.toLowerCase());

    const filtered = jobs
      .slice()
      .reverse()
      .filter(
        (job) =>
          matchesCategory(job) &&
          matchesLocation(job) &&
          matchesTitle(job) &&
          matchesSearchLocation(job)
      );

    setFilteredJobs(filtered);
    setCurrentPage(1);
  }, [jobs, selectedCategories, selectedLocations, searchFilter]);

  const handleCategoryChange = (category) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const handleLocationChange = (location) => {
    setSelectedLocations((prev) =>
      prev.includes(location)
        ? prev.filter((l) => l !== location)
        : [...prev, location]
    );
  };

  return (
    <div className="container 2xl:px-20 mx-auto flex flex-col lg:flex-row max-lg:space-y-8 py-8">
      {/* Sidebar */}
      <div className="w-full lg:w-1/4 bg-white px-4">
        {/* Search Filter (from Hero section) */}

        {isSearched &&
          (searchFilter.title !== "" || searchFilter.location !== "") && (
            <>
              <h3 className="font-medium text-lg mb-4">Current Search</h3>
              <div className="mb-4 text-gray-600 flex flex-wrap gap-2">
                {searchFilter.title && (
                  <span className="inline-flex items-center gap-2.5 bg-blue-50 border border-blue-200 px-4 py-1.5 rounded">
                    {searchFilter.title}
                    <img
                      onClick={() => onClearFilter("title")}
                      className="cursor-pointer w-3 h-3"
                      src={assets.cross_icon}
                      alt="clear title"
                    />
                  </span>
                )}
                {searchFilter.location && (
                  <span className="inline-flex items-center gap-2.5 bg-red-50 border border-red-200 px-4 py-1.5 rounded">
                    {searchFilter.location}
                    <img
                      onClick={() => onClearFilter("location")}
                      className="cursor-pointer w-3 h-3"
                      src={assets.cross_icon}
                      alt="clear location"
                    />
                  </span>
                )}
              </div>
            </>
          )}

        <button
          onClick={() => setShowFilter((prev) => !prev)}
          className="px-6 py-1.5 rounded border border-gray-400 lg:hidden"
        >
          {showFilter ? "Close" : "Filters"}
        </button>

        <div className={showFilter ? "" : "max-lg:hidden"}>
          {/* Category Filter */}
          <h4 className="font-medium text-lg py-4">Search by Categories</h4>
          <ul className="space-y-4 text-gray-600">
            {JobCategories.map((category, index) => (
              <li className="flex gap-3 items-center" key={index}>
                <input
                  className="scale-125"
                  type="checkbox"
                  onChange={() => handleCategoryChange(category)}
                  checked={selectedCategories.includes(category)}
                />
                {category}
              </li>
            ))}
          </ul>

          {/* Location Filter */}
          <h4 className="font-medium text-lg py-4 pt-14">
            Search by Locations
          </h4>
          <ul className="space-y-4 text-gray-600">
            {JobLocations.map((location, index) => (
              <li className="flex gap-3 items-center" key={index}>
                <input
                  className="scale-125"
                  type="checkbox"
                  onChange={() => handleLocationChange(location)}
                  checked={selectedLocations.includes(location)}
                />
                {location}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Job listings */}
      <section className="w-full lg:w-3/4 text-gray-800 max-lg:px-4">
        <h3 className="font-medium text-3xl py-2" id="job-list">
          Latest Jobs
        </h3>
        <p className="mb-8">Get your desired job from top companies</p> {/* ✅ Loading State */}
        {loading ? (
          <Loading />
        ) : filteredJobs.length === 0 ? (
          <div className="text-center text-gray-600 mt-24 text-lg border-2 p-14">
            No jobs found for your search  ❌
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredJobs
                .slice((currentPage - 1) * 6, currentPage * 6)
                .map((job, index) => (
                  <JobCard key={index} job={job} />
                ))}
            </div>

            {/* Pagination */}
            {filteredJobs.length > 0 && (
              <div className="flex items-center justify-center space-x-2 mt-10">
                <a href="#job-list">
                  <img
                    onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                    src={assets.left_arrow_icon}
                    alt=""
                  />
                </a>
                {Array.from({ length: Math.ceil(filteredJobs.length / 6) }).map(
                  (_, index) => (
                    <a key={index} href="#job-list">
                      <button
                        onClick={() => setCurrentPage(index + 1)}
                        className={`w-10 h-10 flex items-center justify-center border border-gray-300 rounded ${
                          currentPage === index + 1
                            ? "bg-blue-100 text-blue-500"
                            : "text-gray-500"
                        }`}
                      >
                        {index + 1}
                      </button>
                    </a>
                  )
                )}
                <a href="#job-list">
                  <img
                    onClick={() =>
                      setCurrentPage(
                        Math.min(
                          currentPage + 1,
                          Math.ceil(filteredJobs.length / 6)
                        )
                      )
                    }
                    src={assets.right_arrow_icon}
                    alt=""
                  />
                </a>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default JobListing;
